import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import axios from "axios";
import { toast } from "react-toastify";
import CartIcon from "../../components/common/CartIcon";

const apiUrl = import.meta.env.VITE_APP_API;

function BookDetail() {
    const { bookId } = useParams();
    const navigate = useNavigate();
    const [book, setBook] = useState(null);
    const [similarBooks, setSimilarBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('description');

    useEffect(() => {
        fetchBookDetails();
    }, [bookId]);

    const fetchBookDetails = async () => {
        try {
            setLoading(true);
            setError(null);

            const bookResponse = await axios.get(`${apiUrl}/api/bookstore/api/books/${bookId}/`);
            const bookData = bookResponse.data;
            setBook(bookData);

            const similarResponse = await axios.get(`${apiUrl}/api/bookstore/api/categories/${bookData.category.id}/books/`);
            const similarData = similarResponse.data || similarResponse.data;
            const filteredSimilar = similarData.filter(b => b.id !== bookData.id).slice(0, 4);
            setSimilarBooks(filteredSimilar);

        } catch (error) {
            console.error('Error fetching book details:', error);
            setError('Failed to load book details. Please try again later.');
            toast.error('Failed to load book details');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 mb-4"></div>
                    <p className="text-gray-600 text-lg font-medium">Loading book details...</p>
                </div>
            </div>
        );
    }

    if (error || !book) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="text-center">
                    <div className="text-red-500 mb-4">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Book</h3>
                    <p className="text-gray-500 mb-4">{error || 'Book not found'}</p>
                    <Link
                        to="/book-store"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 text-sm font-medium"
                    >
                        Back to Book Store
                    </Link>
                </div>
            </div>
        );
    }

    const discountedPrice = book.price - book.discount;
    const isInStock = book.total_amount > 0;

    const handleAddToCart = () => {
        if (!isInStock) return;
        
        const currentCart = JSON.parse(localStorage.getItem('cartItems') || '[]');
        const existingItemIndex = currentCart.findIndex(item => item.id === book.id);
        
        if (existingItemIndex >= 0) {
            currentCart[existingItemIndex].quantity += 1;
        } else {
            currentCart.push({
                id: book.id,
                name: book.name,
                price: book.price,
                discount: book.discount,
                image: book.image,
                quantity: 1
            });
        }
        
        localStorage.setItem('cartItems', JSON.stringify(currentCart));
        const totalCount = currentCart.reduce((sum, item) => sum + item.quantity, 0);
        localStorage.setItem('cartCount', totalCount.toString());
        
        if (window.updateCartCount) {
            window.updateCartCount(totalCount);
        }
        
        toast.success('Book added to cart!');
        
        // Redirect to cart page
        
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8 mt-20">
            <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <Link
                        to="/book-store"
                        className="inline-flex items-center px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-all duration-200 text-sm font-medium"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                        Back to Book Store
                    </Link>
                    <CartIcon />
                </div>

                <div className="bg-white rounded-2xl shadow-xl p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div className="space-y-4">
                            <div className="relative">
                                {book.image ? (
                                    <img
                                        src={book.image}
                                        alt={book.name}
                                        className="w-full h-96 object-cover rounded-lg"
                                        onError={(e) => {
                                            e.target.src = '/images/book-placeholder.jpg';
                                        }}
                                    />
                                ) : (
                                    <div className="w-full h-96 bg-gray-100 rounded-lg flex items-center justify-center">
                                        <span className="text-gray-400 text-lg">No Image Available</span>
                                    </div>
                                )}
                                <div className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1 rounded-full font-semibold text-sm">
                                    মূল্য: {discountedPrice.toFixed(0)}
                                </div>
                            </div>
                            {/* <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold transition-all duration-200">
                                পড়ে দেখুন
                            </button> */}
                        </div>

                        <div className="space-y-6">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 mb-2">{book.name}</h1>
                                <p className="text-gray-600 mb-1">Author: {book.author}</p>
                                <p className="text-gray-600 mb-2">Publisher: {book.publisher}</p>
                                <div className="mb-4">
                                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                                        {book.category_name || 'Book Category'}
                                    </span>
                                </div>
                            </div>

                            <div className="space-y-2">
                                {book.discount > 0 ? (
                                    <div className="flex items-center space-x-3">
                                        <p className="text-gray-400 line-through text-lg">Tk. {book.price.toFixed(0)}</p>
                                        <p className="text-green-600 font-bold text-2xl">Tk. {discountedPrice.toFixed(0)}</p>
                                    </div>
                                ) : (
                                    <p className="text-green-600 font-bold text-2xl">Tk. {book.price.toFixed(0)}</p>
                                )}
                            </div>

                            <div className="flex items-center space-x-2">
                                <div className={`w-3 h-3 rounded-full ${isInStock ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                <span className={`font-medium ${isInStock ? 'text-green-600' : 'text-red-600'}`}>
                                    {isInStock ? 'In Stock' : 'Out of Stock'}
                                </span>
                            </div>

                            <div className="space-y-3">
                                <button
                                    onClick={()=>{handleAddToCart() , navigate('/cart');}}
                                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                                        isInStock
                                            ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                    }`}
                                    disabled={!isInStock}
                                >
                                    Buy Now
                                </button>
                                <button
                                    onClick={handleAddToCart}
                                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                                        isInStock
                                            ? 'bg-pink-600 hover:bg-pink-700 text-white'
                                            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                    }`}
                                    disabled={!isInStock}
                                >
                                    Add to Cart
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-xl p-6 mt-8">
                    <div className="border-b border-gray-200 mb-6">
                        <nav className="flex space-x-8">
                            {['description', 'qa', 'reviews'].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`py-3 px-1 border-b-2 font-medium text-sm transition-all duration-200 ${
                                        activeTab === tab
                                            ? 'border-blue-600 text-blue-600'
                                            : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
                                    }`}
                                >
                                    {tab === 'description' && 'Description'}
                                    {tab === 'qa' && 'Q & A'}
                                    {tab === 'reviews' && 'Reviews'}
                                </button>
                            ))}
                        </nav>
                    </div>

                    <div className="prose max-w-none">
                        {activeTab === 'description' && (
                            <div>
                                <p className="text-gray-700 leading-relaxed">
                                    {book.description || 'আসপেক্ট মেডিকেল বায়োলজি, কেমিস্ট্রি, ফিজিক্স, ইংলিশ এবং জিকে বইটি। যার সবগুলো একত্রে মূল্য ২৬৫০/- সাথে পাচ্ছো আকর্ষণীয় একটি টি-শার্ট এবং ডেলিভারি চার্জ সম্পূর্ণ ফ্রি!'}
                                </p>
                            </div>
                        )}
                        {activeTab === 'qa' && (
                            <div className="text-center py-8">
                                <p className="text-gray-500">Q&A section coming soon...</p>
                            </div>
                        )}
                        {activeTab === 'reviews' && (
                            <div className="text-center py-8">
                                <p className="text-gray-500">Reviews section coming soon...</p>
                            </div>
                        )}
                    </div>
                </div>

                {similarBooks.length > 0 && (
                    <div className="bg-white rounded-2xl shadow-xl p-6 mt-8">
                        <h2 className="text-2xl font-bold text-center text-gray-900 mb-6">Similar Books</h2>
                        <div className="relative">
                            <Swiper
                                modules={[Navigation, Pagination]}
                                navigation
                                pagination={{ clickable: true }}
                                spaceBetween={20}
                                slidesPerView={1}
                                breakpoints={{
                                    640: { slidesPerView: 2 },
                                    768: { slidesPerView: 3 },
                                    1024: { slidesPerView: 4 },
                                }}
                            >
                                {similarBooks.map((similarBook) => (
                                    <SwiperSlide key={similarBook.id}>
                                        <Link to={`/book-store/${similarBook.id}`}>
                                            <div className="border border-gray-200 rounded-lg p-4 bg-white hover:shadow-lg transition-all duration-200 h-full flex flex-col">
                                                <div className="relative">
                                                    {similarBook.image ? (
                                                        <img
                                                            src={`${apiUrl}${similarBook.image}`}
                                                            alt={similarBook.name}
                                                            className="w-full h-48 object-cover rounded-lg mb-3"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-48 bg-gray-100 rounded-lg mb-3 flex items-center justify-center">
                                                            <span className="text-gray-400 text-sm">No Image</span>
                                                        </div>
                                                    )}
                                                    <div className="absolute top-2 right-2">
                                                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                            similarBook.total_amount === 0
                                                                ? 'bg-red-100 text-red-800'
                                                                : similarBook.total_amount < 5
                                                                    ? 'bg-yellow-100 text-yellow-800'
                                                                    : 'bg-green-100 text-green-800'
                                                        }`}>
                                                            {similarBook.total_amount === 0 ? 'Out of Stock' : `${similarBook.total_amount} left`}
                                                        </span>
                                                    </div>
                                                </div>
                                                <h3 className="text-sm font-semibold text-gray-900 flex-grow mb-2">{similarBook.name}</h3>
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        {similarBook.discount > 0 ? (
                                                            <div>
                                                                <span className="text-gray-400 line-through text-xs">
                                                                    Tk. {similarBook.price.toFixed(0)}
                                                                </span>
                                                                <p className="text-pink-600 font-semibold">
                                                                    Tk. {(similarBook.price - similarBook.discount).toFixed(0)}
                                                                </p>
                                                            </div>
                                                        ) : (
                                                            <p className="text-pink-600 font-semibold">
                                                                Tk. {similarBook.price.toFixed(0)}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </Link>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default BookDetail;